import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { ErrorLog } from '@/types';

interface ErrorLogViewerProps {
  logs: ErrorLog[];
  onClear: () => void;
}

export default function ErrorLogViewer({ logs, onClear }: ErrorLogViewerProps) {
  const [expandedLog, setExpandedLog] = React.useState<string | null>(null);

  const logsByLevel = {
    critical: logs.filter((log) => log.level === 'critical'),
    error: logs.filter((log) => log.level === 'error'),
    warning: logs.filter((log) => log.level === 'warning'),
    info: logs.filter((log) => log.level === 'info'),
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.stat}>
            <span style={{ ...styles.statBadge, backgroundColor: '#742a2a' }}>
              {logsByLevel.critical.length}
            </span>
            <span style={styles.statLabel}>Critical</span>
          </div>
          <div style={styles.stat}>
            <span style={{ ...styles.statBadge, backgroundColor: '#c53030' }}>
              {logsByLevel.error.length}
            </span>
            <span style={styles.statLabel}>Errors</span>
          </div>
          <div style={styles.stat}>
            <span style={{ ...styles.statBadge, backgroundColor: '#dd6b20' }}>
              {logsByLevel.warning.length}
            </span>
            <span style={styles.statLabel}>Warnings</span>
          </div>
        </div>

        <button onClick={onClear} style={styles.clearButton}>
          Clear All Logs
        </button>
      </div>

      <div style={styles.logList}>
        {logs.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>No error logs</p>
          </div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              style={{
                ...styles.logEntry,
                borderLeftColor: getLevelColor(log.level),
              }}
            >
              <div
                style={styles.logHeader}
                onClick={() =>
                  setExpandedLog(expandedLog === log.id ? null : log.id)
                }
              >
                <div style={styles.logHeaderLeft}>
                  <span
                    style={{
                      ...styles.levelBadge,
                      backgroundColor: getLevelColor(log.level),
                    }}
                  >
                    {log.level.toUpperCase()}
                  </span>
                  <span style={styles.timestamp}>
                    {formatDistanceToNow(new Date(log.timestamp), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <button style={styles.expandButton}>
                  {expandedLog === log.id ? '−' : '+'}
                </button>
              </div>

              <p style={styles.message}>{log.message}</p>

              {expandedLog === log.id && (
                <div style={styles.details}>
                  {log.context && (
                    <div style={styles.context}>
                      <strong style={styles.detailLabel}>Context:</strong>
                      <pre style={styles.pre}>
                        {JSON.stringify(log.context, null, 2)}
                      </pre>
                    </div>
                  )}

                  {log.stackTrace && (
                    <div style={styles.stackTrace}>
                      <strong style={styles.detailLabel}>Stack Trace:</strong>
                      <pre style={styles.pre}>{log.stackTrace}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getLevelColor(level: string): string {
  switch (level) {
    case 'critical':
      return '#742a2a';
    case 'error':
      return '#c53030';
    case 'warning':
      return '#dd6b20';
    case 'info':
      return '#3182ce';
    default:
      return '#718096';
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '2px solid #e2e8f0',
  },
  stats: {
    display: 'flex',
    gap: '16px',
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
  },
  statLabel: {
    fontSize: '14px',
    color: '#4a5568',
    fontWeight: '500',
  },
  clearButton: {
    padding: '8px 16px',
    backgroundColor: '#f56565',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  logList: {
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  empty: {
    padding: '40px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  logEntry: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  logHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    cursor: 'pointer',
  },
  logHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  levelBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    color: 'white',
    fontSize: '11px',
    fontWeight: '700',
  },
  timestamp: {
    fontSize: '13px',
    color: '#718096',
  },
  expandButton: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  message: {
    margin: 0,
    fontSize: '14px',
    color: '#2d3748',
    lineHeight: '1.5',
  },
  details: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  context: {
    marginBottom: '12px',
  },
  stackTrace: {},
  detailLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#4a5568',
    marginBottom: '6px',
  },
  pre: {
    margin: 0,
    padding: '12px',
    backgroundColor: '#1a202c',
    color: '#e2e8f0',
    borderRadius: '6px',
    fontSize: '12px',
    fontFamily: 'monospace',
    overflowX: 'auto',
    lineHeight: '1.5',
  },
};
